import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { ABSENCE_TYPE_OPTIONS } from "@/lib/employeeAbsences";
import {
  getCalendarSettings,
  listGermanStates,
  updateCalendarSettings,
  type BridgeWednesdaySide,
  type EmployeeTimeCalendarSettings,
  type GermanStateOption,
} from "@/lib/employeeCalendar";

interface CalendarSettingsPanelProps {
  onChanged?: () => void;
}

export function CalendarSettingsPanel({ onChanged }: CalendarSettingsPanelProps) {
  const [states, setStates] = useState<GermanStateOption[]>([]);
  const [form, setForm] = useState<EmployeeTimeCalendarSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, stateList] = await Promise.all([
        getCalendarSettings(),
        listGermanStates(),
      ]);
      setForm(settings);
      setStates(stateList);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Kalender-Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const saved = await updateCalendarSettings({
        bundesland: form.bundesland,
        bridge_days_enabled: form.bridge_days_enabled,
        bridge_default_absence_type: form.bridge_default_absence_type,
        bridge_wednesday_double: form.bridge_wednesday_double,
        bridge_wednesday_side: form.bridge_wednesday_side,
      });
      setForm(saved);
      toast.success("Kalender-Einstellungen gespeichert.");
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="rounded-ios border border-separator bg-card p-5 shadow-ios">
        <h3 className="text-lg font-semibold text-label">Feiertage, Brückentage &amp; Betriebsferien</h3>
        <p className="mt-1 text-sm text-label-secondary">
          Feiertage werden anhand des Bundeslands geladen. Betriebsferien gelten immer von der KW mit
          dem 24.12. bis einschließlich der KW mit dem 1.1. (Heiligabend/Silvester: ½ Tag). Für
          Brückentage und Betriebsferien wird Urlaub automatisch genehmigt; Abweichungen per Antrag.
        </p>

        <div className="mt-5 space-y-4">
          <label className="block space-y-1">
            <span className="eh-label">Bundesland</span>
            <select
              className="eh-input w-full"
              value={form.bundesland}
              onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
            >
              {states.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </label>

          <label className="flex items-start gap-3 rounded-lg border border-separator p-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={form.bridge_days_enabled}
              onChange={(e) => setForm({ ...form, bridge_days_enabled: e.target.checked })}
            />
            <span>
              <span className="block font-medium text-label">Brückentage in der Firma nutzen</span>
              <span className="text-sm text-label-secondary">
                z. B. Freitag nach Himmelfahrt (Do), Montag vor Fronleichnam (Di)
              </span>
            </span>
          </label>

          {form.bridge_days_enabled && (
            <>
              <label className="block space-y-1">
                <span className="eh-label">Standard-Abwesenheit für Brückentage</span>
                <select
                  className="eh-input w-full"
                  value={form.bridge_default_absence_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      bridge_default_absence_type: e.target.value as EmployeeTimeCalendarSettings["bridge_default_absence_type"],
                    })
                  }
                >
                  {ABSENCE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-separator p-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.bridge_wednesday_double}
                  onChange={(e) => setForm({ ...form, bridge_wednesday_double: e.target.checked })}
                />
                <span>
                  <span className="block font-medium text-label">Bei Feiertag am Mittwoch zwei Brückentage</span>
                  <span className="text-sm text-label-secondary">
                    z. B. Fronleichnam: Montag+Dienstag oder Donnerstag+Freitag
                  </span>
                </span>
              </label>

              {form.bridge_wednesday_double && (
                <fieldset className="space-y-2 rounded-lg border border-separator p-3">
                  <legend className="px-1 text-sm font-medium text-label">
                    Welche zwei Tage bei Mittwochs-Feiertag?
                  </legend>
                  <label className="flex items-center gap-2 text-sm text-label">
                    <input
                      type="radio"
                      name="wednesday_side"
                      checked={form.bridge_wednesday_side === "before"}
                      onChange={() => setForm({ ...form, bridge_wednesday_side: "before" as BridgeWednesdaySide })}
                    />
                    Erste zwei Tage (Montag + Dienstag vor dem Feiertag)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-label">
                    <input
                      type="radio"
                      name="wednesday_side"
                      checked={form.bridge_wednesday_side === "after"}
                      onChange={() => setForm({ ...form, bridge_wednesday_side: "after" as BridgeWednesdaySide })}
                    />
                    Letzte zwei Tage (Donnerstag + Freitag nach dem Feiertag)
                  </label>
                </fieldset>
              )}
            </>
          )}
        </div>


        <div className="mt-5">
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </div>

      <div className="rounded-ios border border-dashed border-separator bg-card/60 p-4 text-sm text-label-secondary">
        <p className="font-medium text-label">Hinweis für Mitarbeiter</p>
        <p className="mt-1">
          Brückentage und Betriebsferien erscheinen in Montageplanung, Monats- und Jahresübersicht
          automatisch als genehmigter Urlaub (bzw. der gewählte Standard-Typ). Ein abweichender Antrag
          (z. B. Überstundenabbau) oder „An diesem Tag arbeiten“ überschreibt die Voreinstellung.
        </p>
      </div>
    </div>
  );
}
